/**
 * Editor state (Zustand).
 *
 * Holds the caption model (words + segments), the chosen template and style
 * overrides, selection, and an action-level undo/redo history. History snapshots
 * are taken per committed ACTION (text edits commit on blur/Enter), never per
 * keystroke - see ARCHITECTURE.md / spec "undo without giant JSON snapshots".
 */
import { create } from 'zustand';
import {
  segmentTranscript,
  sanitizeWordTimings,
  correctWordText,
  setWordHidden,
  setWordEmphasis,
  enforceSingleEmphasisPerSegment,
  splitSegment as splitSeg,
  mergeSegments as mergeSeg,
  resplitSegment,
  normalizeHooks,
  readableTextColor,
  colorLuminance,
  localId,
  type HookMoment,
  type SubjectTrack,
  type WordStyle,
  type TranscriptWord,
  type CaptionSegment,
  type CaptionStyle,
  type CaptionPlacement,
  type TemplateConfig,
} from '@captionflow/caption-engine';
import { TEMPLATES_BY_ID, DEFAULT_TEMPLATE_ID } from '@captionflow/template-schema';
import type { TransitionDTO, MagicResult } from '@captionflow/shared';

export type SaveState = 'saved' | 'saving' | 'dirty';
/** Output aspect ratios; 'source' keeps the clip's native shape. */
export type AspectKey = '9:16' | '16:9' | '1:1' | '4:5' | 'source';
export type { HookMoment };

/** Preview person cut-out (+ the head track derived from it). */
export interface CutoutUi {
  status: 'idle' | 'running' | 'ready' | 'failed';
  url: string | null;
  progress: number;
  /** Head track for hooks that follow the subject (null until derived). */
  track: SubjectTrack | null;
}
export type Transition = TransitionDTO;
export type TransitionKind = TransitionDTO['kind'];

export interface MusicTrack {
  name: string;
  /** Playback URL (blob while uploading, presigned after reload, or external). */
  url: string;
  /** Durable object key once uploaded - required to persist an uploaded file. */
  storageKey?: string;
  volume: number;
  loop: boolean;
  /** Lower the music while someone is speaking. */
  duck: boolean;
  /** Time window the music plays over (defaults to the whole clip). */
  startMs?: number;
  durationMs?: number;
}
/** A timed sound-effect: a one-shot audio clip placed at a moment for a duration
 *  (e.g. a dog bark at 3s). Unlike music it does NOT loop and there can be many. */
export interface SfxClip {
  id: string;
  name: string;
  /** Playback URL (external/presigned). */
  url: string;
  /** Durable object key once stored - required to persist + export. */
  storageKey?: string;
  startMs: number;
  durationMs: number;
  volume: number;
}
/** Per-project audio: the ORIGINAL video track's level, a background-music
 *  track, and a list of timed sound effects. */
export interface AudioSettings {
  originalVolume: number;
  originalMuted: boolean;
  music: MusicTrack | null;
  sfx: SfxClip[];
}

/** A B-roll clip: an uploaded image/video shown over a time window, either
 *  covering the whole frame or as a movable/resizable overlay. Media is a
 *  session object URL (upload/export is a follow-up). */
export interface Broll {
  id: string;
  kind: 'image' | 'video';
  name: string;
  /** Preview URL (blob while uploading, presigned after reload). */
  url: string;
  /** Durable object key once uploaded - required to persist across refresh. */
  storageKey?: string;
  startMs: number;
  durationMs: number;
  mode: 'cover' | 'overlay';
  /** Overlay placement: centre (0..1) + width as a fraction of the frame. */
  x: number;
  y: number;
  scale: number;
  fit: 'cover' | 'contain';
}

/** As-created project snapshot (from the enhance stage), used by the kMagic
 *  "Reset to original" button to restore the default template + auto breaks +
 *  the rare AI emphasis. Null on legacy projects made before it was captured. */
export interface InitialSnapshot {
  templateId?: string;
  emphasizedWordIds?: string[];
  segments?: { idx: number; wordIds: string[]; startMs: number; endMs: number }[];
}

export type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

interface Snapshot {
  words: TranscriptWord[];
  segments: CaptionSegment[];
  templateId: string;
  styleOverrides: DeepPartial<CaptionStyle>;
  placementOverride: Partial<CaptionPlacement>;
  transitions: Transition[];
}

export type TimingMode = 'manual' | 'auto';

interface EditorState extends Snapshot {
  durationMs: number;
  videoUrl: string | null;
  projectId: string | null;
  /** BCP-47-ish language of the transcript (for the AI enhance prompt). */
  language: string | null;
  selectedWordId: string | null;
  /** Multi-word selection (studio timeline) for bulk effects. */
  selectedWordIds: string[];
  setSelectedWords: (ids: string[]) => void;
  toggleWordSelected: (id: string) => void;
  clearWordSelection: () => void;
  /** Segment currently hovered (timeline ↔ transcript cross-highlight). */
  hoverSegmentId: string | null;
  setHoverSegment: (id: string | null) => void;
  saveState: SaveState;
  /** manual (default): free timing, overlaps allowed + flagged. auto: clamp to neighbours. */
  timingMode: TimingMode;
  past: Snapshot[];
  future: Snapshot[];

  // derived
  template: () => TemplateConfig;
  resolvedStyle: () => CaptionStyle;
  resolvedPlacement: () => CaptionPlacement;

  // derived: per-line style resolution (a line may override the project template)
  templateForSegment: (seg: CaptionSegment) => TemplateConfig;
  styleForSegment: (seg: CaptionSegment) => CaptionStyle;
  placementForSegment: (seg: CaptionSegment) => CaptionPlacement;

  /** Timeline transitions + which one is selected in the panel. */
  selectedTransitionId: string | null;
  addTransition: (kind: TransitionKind, startMs: number) => void;
  updateTransition: (id: string, patch: Partial<Omit<Transition, 'id'>>) => void;
  removeTransition: (id: string) => void;
  selectTransition: (id: string | null) => void;

  // lifecycle
  load: (init: {
    words: TranscriptWord[];
    segments?: CaptionSegment[];
    templateId?: string;
    durationMs: number;
    videoUrl?: string | null;
    transitions?: Transition[];
    styleOverrides?: DeepPartial<CaptionStyle>;
    placementOverride?: Partial<CaptionPlacement>;
    aspect?: AspectKey;
    sourceDims?: { w: number; h: number } | null;
  }) => void;

  // edits
  selectWord: (id: string | null) => void;
  editWordText: (id: string, text: string) => void;
  setWordsText: (entries: { id: string; text: string }[]) => void;
  toggleHidden: (id: string) => void;
  toggleEmphasis: (id: string) => void;
  /** Per-word color override (hex), or null to clear back to the template. */
  setWordColor: (id: string, color: string | null) => void;
  setWordSizeScale: (id: string, scale: number) => void;
  setWordBold: (id: string, bold: boolean) => void;
  resetWordStyle: (id: string) => void;
  /** Apply a style patch to many words at once (multi-selection). */
  styleWords: (
    ids: string[],
    patch: {
      emphasized?: boolean;
      bold?: boolean;
      sizeScale?: number;
      colorOverride?: string | null;
      hidden?: boolean;
      /** Merged into the word's own style; a null value clears that field. */
      wordStyle?: Partial<{ [K in keyof WordStyle]: WordStyle[K] | null }>;
    },
  ) => void;
  resetWordsStyle: (ids: string[]) => void;
  setTemplate: (id: string) => void;
  /** Apply an AI (or fallback) enhancement: replace caption breaks with the
   *  returned groups and emphasize only the (rare) groups that earned a key
   *  word - groups with keyWordId null get no emphasis. */
  applyEnhancement: (groups: { wordIds: string[]; keyWordId: string | null }[]) => void;
  /** Caption lines selected for a per-line style change (empty = whole video). */
  selectedSegmentIds: string[];
  setSelectedSegments: (ids: string[]) => void;
  toggleSegmentSelected: (id: string, additive: boolean) => void;
  clearSegmentSelection: () => void;
  /** Assign a template to specific lines (per-window style), or null to clear
   *  back to the project default. */
  setSegmentTemplate: (segmentIds: string[], templateId: string | null) => void;
  updateStyle: (patch: DeepPartial<CaptionStyle>) => void;
  setPlacement: (patch: Partial<CaptionPlacement>) => void;
  resetStyle: () => void;
  /** Fixed number of words shown on screen at a time. null = auto (the template's
   *  phrase-aware grouping). Re-segments the whole transcript into N-word groups
   *  so a caption hides as soon as its N words are done. */
  wordsPerGroup: number | null;
  setWordsPerGroup: (n: number | null) => void;
  /** The as-created snapshot (set on load), and a reset that restores the whole
   *  project look/structure to it (template, breaks, emphasis; clears overrides,
   *  hooks, B-roll, audio, per-word styles). Keeps the current word TEXT. */
  initialSnapshot: InitialSnapshot | null;
  resetToInitial: () => void;

  /** Audio settings - original track level + an optional background-music track.
   *  Session/preview state (not undoable, not yet exported). */
  audio: AudioSettings;
  setOriginalVolume: (v: number) => void;
  setOriginalMuted: (m: boolean) => void;
  setMusic: (name: string, url: string) => void;
  updateMusic: (patch: Partial<MusicTrack>) => void;
  removeMusic: () => void;
  /** Timed sound effects (one-shot audio at a moment for a duration). */
  addSfx: (clip: Omit<SfxClip, 'id'>) => string;
  updateSfx: (id: string, patch: Partial<Omit<SfxClip, 'id'>>) => void;
  removeSfx: (id: string) => void;
  /** Master OUTPUT level (bottom transport) - the final volume of everything
   *  composited (original + music). A multiplier over each track's own volume.
   *  Session-only monitoring level (not persisted). */
  masterVolume: number;
  masterMuted: boolean;
  setMasterVolume: (v: number) => void;
  setMasterMuted: (m: boolean) => void;

  /** B-roll overlays (session/preview state, not yet exported). */
  brolls: Broll[];
  selectedBrollId: string | null;
  /** Hook moments / power-word takeovers - rare (≤4), big, center-screen text
   *  events with their own time window that can hide the running captions.
   *  Persisted + rendered in exports (shares the engine draw with the preview). */
  hooks: HookMoment[];
  selectedHookId: string | null;
  addHook: (partial?: Partial<HookMoment>) => string;
  updateHook: (id: string, patch: Partial<Omit<HookMoment, 'id'>>) => void;
  removeHook: (id: string) => void;
  selectHook: (id: string | null) => void;
  /** Which left-sidebar tab is open (drives the timeline's B-roll lane). */
  leftTab: string;
  setLeftTab: (tab: string) => void;
  /** Safe-zone guide overlay: draws the platform's UI chrome as placeholders on
   *  the stage so captions avoid it. View-only guide - not persisted/exported. */
  safeZone: 'off' | 'youtube' | 'instagram';
  setSafeZone: (z: 'off' | 'youtube' | 'instagram') => void;
  /** Output aspect ratio for the stage preview + export. Auto-detected from the
   *  source at ingest, and overridable here. 'source' uses the clip's own shape
   *  (see `sourceDims`). Persisted on the project. */
  aspect: AspectKey;
  /** Native source dimensions, used to preview the 'source' ratio faithfully. */
  sourceDims: { w: number; h: number } | null;
  setAspect: (a: AspectKey) => void;
  /** Preview person cut-out (the "behind the person" 3D depth). Generated once
   *  on the server and cached; `url` is the alpha WebM the stage plays as the
   *  person layer. `dialogOpen` gates the "generate this?" prompt. */
  cutout: CutoutUi;
  setCutout: (patch: Partial<CutoutUi>) => void;
  /** Studio-sound audio enhancer: a background job cleans the original speech;
   *  `url` is the enhanced track the preview A/Bs and the export swaps in when
   *  `enabled`. `strength`/`loudnorm` are the chosen params. */
  audioEnhance: {
    status: 'idle' | 'running' | 'ready' | 'failed';
    progress: number;
    enabled: boolean;
    strength: number;
    loudnorm: number;
    url: string | null;
    stale: boolean;
    error: string | null;
  };
  setAudioEnhance: (patch: Partial<EditorState['audioEnhance']>) => void;
  depthDialogOpen: boolean;
  setDepthDialogOpen: (open: boolean) => void;
  /** Export preference: burn in the Kaption watermark. Free/trial plans force
   *  this on server-side regardless; paid plans control it from the top bar. */
  watermark: boolean;
  setWatermark: (on: boolean) => void;
  /** kMagic preview: applies an AI result to the editor, keeping a backup so it
   *  can be rolled back to the user's own version, or committed (kept + saved). */
  magicPreview: { backup: { snap: Snapshot; brolls: Broll[]; audio: AudioSettings; hooks: HookMoment[] } } | null;
  previewMagicResult: (result: MagicResult) => void;
  commitMagicPreview: () => void;
  rollbackMagicPreview: () => void;

  addBroll: (kind: Broll['kind'], name: string, url: string, startMs: number) => string;
  updateBroll: (id: string, patch: Partial<Omit<Broll, 'id' | 'kind'>>) => void;
  removeBroll: (id: string) => void;
  selectBroll: (id: string | null) => void;
  splitSegmentAt: (segmentId: string, afterWordIndex: number) => void;
  mergeSegmentWithNext: (segmentId: string) => void;
  /** Edit a whole caption line's text; auto re-splits into timed words. */
  editSegmentText: (segmentId: string, newText: string) => void;
  /** Insert a new caption line in the time gap after the given segment. Returns the new id. */
  insertSegmentAfter: (segmentId: string) => string | null;
  /** Set a caption line's start and/or end time. In 'auto' mode it clamps to
   *  neighbours; in 'manual' mode overlaps are allowed (and flagged in the UI).
   *  Member words are linearly remapped into the new window. */
  setSegmentTiming: (segmentId: string, patch: { startMs?: number; endMs?: number }) => void;
  setTimingMode: (mode: TimingMode) => void;
  /** Timeline drag: call once before a drag, then dragSegmentTiming during it. */
  beginTimingDrag: () => void;
  dragSegmentTiming: (segmentId: string, patch: { startMs?: number; endMs?: number }) => void;
  /** Delete a caption line (its words are removed from the transcript). */
  deleteSegment: (segmentId: string) => void;

  undo: () => void;
  redo: () => void;
  markSaved: () => void;
}

function deepMerge<T>(base: T, patch: DeepPartial<T>): T {
  const out: Record<string, unknown> = Array.isArray(base)
    ? ([...(base as unknown[])] as never)
    : { ...(base as object) };
  for (const key of Object.keys(patch as object)) {
    const p = (patch as Record<string, unknown>)[key];
    const b = (base as Record<string, unknown>)[key];
    if (p && typeof p === 'object' && !Array.isArray(p) && b && typeof b === 'object') {
      out[key] = deepMerge(b, p as DeepPartial<typeof b>);
    } else if (p !== undefined) {
      out[key] = p;
    }
  }
  return out as T;
}

/** Pure timing computation shared by setSegmentTiming (committed) and drag (live). */
function computeSegmentTiming(
  s: EditorState,
  segmentId: string,
  patch: { startMs?: number; endMs?: number },
): Partial<EditorState> {
  const seg = s.segments.find((x) => x.id === segmentId);
  if (!seg) return {};
  let newStart = patch.startMs ?? seg.startMs;
  let newEnd = patch.endMs ?? seg.endMs;
  if (newStart < 0) newStart = 0;
  if (newEnd <= newStart) newEnd = newStart + 1;

  if (s.timingMode === 'auto') {
    const ordered = [...s.segments].sort((a, b) => a.startMs - b.startMs);
    const pos = ordered.findIndex((x) => x.id === segmentId);
    const prev = ordered[pos - 1];
    const next = ordered[pos + 1];
    const lower = prev ? prev.endMs : 0;
    const upper = next ? next.startMs : s.durationMs || newEnd;
    newStart = Math.max(lower, Math.min(newStart, Math.max(lower, upper - 1)));
    newEnd = Math.min(upper, Math.max(newStart + 1, newEnd));
  }

  const oldStart = seg.startMs;
  const oldSpan = Math.max(1, seg.endMs - seg.startMs);
  const scale = (newEnd - newStart) / oldSpan;
  const memberIds = new Set(seg.wordIds);
  const words = s.words.map((w) => {
    if (!memberIds.has(w.id)) return w;
    const ns = Math.round(newStart + (w.startMs - oldStart) * scale);
    const ne = Math.round(newStart + (w.endMs - oldStart) * scale);
    return { ...w, startMs: Math.max(0, ns), endMs: Math.max(ns + 1, ne) };
  });
  const updated: CaptionSegment = { ...seg, startMs: newStart, endMs: newEnd };
  const segments = s.segments
    .map((x) => (x.id === segmentId ? updated : x))
    .sort((a, b) => a.startMs - b.startMs);
  return { words, segments };
}

function snap(s: EditorState): Snapshot {
  return {
    words: s.words,
    segments: s.segments,
    templateId: s.templateId,
    styleOverrides: s.styleOverrides,
    placementOverride: s.placementOverride,
    transitions: s.transitions,
  };
}

const HISTORY_LIMIT = 100;

export const useEditor = create<EditorState>((set, get) => ({
  words: [],
  segments: [],
  templateId: DEFAULT_TEMPLATE_ID,
  styleOverrides: {},
  placementOverride: {},
  transitions: [],
  selectedTransitionId: null,
  audio: { originalVolume: 1, originalMuted: false, music: null, sfx: [] },
  masterVolume: 1,
  masterMuted: false,
  brolls: [],
  selectedBrollId: null,
  hooks: [],
  selectedHookId: null,
  leftTab: 'styles',
  safeZone: 'off',
  aspect: '9:16',
  sourceDims: null,
  cutout: { status: 'idle', url: null, progress: 0, track: null },
  audioEnhance: { status: 'idle', progress: 0, enabled: false, strength: 0.6, loudnorm: -14, url: null, stale: false, error: null },
  depthDialogOpen: false,
  watermark: false,
  magicPreview: null,
  initialSnapshot: null,
  wordsPerGroup: null,
  durationMs: 0,
  videoUrl: null,
  projectId: null,
  language: null,
  selectedWordId: null,
  selectedWordIds: [],
  selectedSegmentIds: [],
  hoverSegmentId: null,
  saveState: 'saved',
  timingMode: 'manual',
  past: [],
  future: [],

  template: () => TEMPLATES_BY_ID[get().templateId] ?? TEMPLATES_BY_ID[DEFAULT_TEMPLATE_ID]!,
  resolvedStyle: () => deepMerge(get().template().style, get().styleOverrides),
  resolvedPlacement: () => ({ ...get().template().placement, ...get().placementOverride }),

  // A line with its own templateId renders in that template's style as-is (the
  // project's global overrides were tuned for the default template, so they are
  // NOT applied on top of a per-line template). Lines without one use the
  // project default template + overrides.
  templateForSegment: (seg) => {
    const s = get();
    if (seg.templateId && TEMPLATES_BY_ID[seg.templateId]) return TEMPLATES_BY_ID[seg.templateId]!;
    return s.template();
  },
  styleForSegment: (seg) => {
    const s = get();
    if (seg.templateId && TEMPLATES_BY_ID[seg.templateId]) return TEMPLATES_BY_ID[seg.templateId]!.style;
    return s.resolvedStyle();
  },
  placementForSegment: (seg) => {
    const s = get();
    if (seg.templateId && TEMPLATES_BY_ID[seg.templateId]) return TEMPLATES_BY_ID[seg.templateId]!.placement;
    return s.resolvedPlacement();
  },

  load: ({ words, segments, templateId, durationMs, videoUrl, transitions, styleOverrides, placementOverride, aspect, sourceDims }) => {
    const tid = templateId ?? DEFAULT_TEMPLATE_ID;
    const tpl = TEMPLATES_BY_ID[tid] ?? TEMPLATES_BY_ID[DEFAULT_TEMPLATE_ID]!;
    // Defensive: clean any overlapping/non-monotonic STT timings so segments
    // (and the timeline) are consistent even for transcripts saved before the
    // ingest-time sanitizer existed.
    const clean = sanitizeWordTimings(words);
    set({
      words: clean,
      // Use SAVED segments (breaks + per-line templates) when provided; else
      // auto-segment from the words with the template's rules.
      segments: segments && segments.length ? segments : segmentTranscript(clean, tpl.segmentation),
      templateId: tid,
      // Restore saved editor overrides + transitions so a refresh keeps them.
      styleOverrides: styleOverrides ?? {},
      placementOverride: placementOverride ?? {},
      transitions: transitions ?? [],
      selectedTransitionId: null,
      aspect: aspect ?? '9:16',
      sourceDims: sourceDims ?? null,
      // Hooks are restored separately (pipeline.loadTranscriptIntoEditor) like
      // brolls/audio; clear here so they never bleed across projects.
      hooks: [],
      selectedHookId: null,
      // NOTE: `cutout` is intentionally NOT reset here - StudioDepthDialog owns
      // its lifecycle (it fetches per-project). Resetting it in load() raced with
      // that fetch and wiped a ready cut-out, so the studio never showed depth.
      depthDialogOpen: false,
      durationMs,
      videoUrl: videoUrl ?? null,
      selectedWordId: null,
      selectedWordIds: [],
      selectedSegmentIds: [],
      saveState: 'saved',
      past: [],
      future: [],
    });
  },

  setSelectedSegments: (ids) => set({ selectedSegmentIds: ids }),
  toggleSegmentSelected: (id, additive) =>
    set((s) => {
      const has = s.selectedSegmentIds.includes(id);
      if (additive) {
        return {
          selectedSegmentIds: has
            ? s.selectedSegmentIds.filter((x) => x !== id)
            : [...s.selectedSegmentIds, id],
        };
      }
      // Non-additive: clicking the only selected line clears; else select just it.
      return { selectedSegmentIds: has && s.selectedSegmentIds.length === 1 ? [] : [id] };
    }),
  clearSegmentSelection: () => set({ selectedSegmentIds: [] }),

  setSegmentTemplate: (segmentIds, templateId) =>
    commit(set, get, (s) => {
      const ids = new Set(segmentIds);
      if (ids.size === 0) return {};
      return {
        segments: s.segments.map((seg) =>
          ids.has(seg.id) ? { ...seg, templateId: templateId ?? undefined } : seg,
        ),
      };
    }),

  selectWord: (id) => set({ selectedWordId: id }),

  // Multi-word selection (transient UI state - not undoable).
  setSelectedWords: (ids) => set({ selectedWordIds: ids }),
  toggleWordSelected: (id) =>
    set((s) => ({
      selectedWordIds: s.selectedWordIds.includes(id)
        ? s.selectedWordIds.filter((x) => x !== id)
        : [...s.selectedWordIds, id],
    })),
  clearWordSelection: () => set({ selectedWordIds: [] }),

  setHoverSegment: (id) => set({ hoverSegmentId: id }),

  editWordText: (id, text) =>
    commit(set, get, (s) => {
      const trimmed = text.trim();
      if (!trimmed) return {}; // ignore empty edits (would create a blank word)
      return { words: correctWordText(s.words, id, trimmed) };
    }),

  // Bulk text replace (e.g. translation): swap word text by id in one undoable
  // commit, preserving timings, segments, emphasis and style overrides.
  setWordsText: (entries) =>
    commit(set, get, (s) => {
      const map = new Map(entries.map((e) => [e.id, e.text.trim()]));
      return {
        words: s.words.map((w) => {
          const t = map.get(w.id);
          return t ? { ...w, text: t } : w;
        }),
      };
    }),

  toggleHidden: (id) =>
    commit(set, get, (s) => {
      const w = s.words.find((x) => x.id === id);
      const words = setWordHidden(s.words, id, !w?.hidden);
      return { words, segments: segmentTranscript(words, s.template().segmentation) };
    }),

  toggleEmphasis: (id) =>
    commit(set, get, (s) => {
      const w = s.words.find((x) => x.id === id);
      const turningOn = !w?.emphasized;
      let words = setWordEmphasis(s.words, id, turningOn);
      // Manual emphasis wins its window: clear any other emphasized word in the
      // same segment so the word the user clicked is the single popped one.
      if (turningOn) {
        const seg = s.segments.find((x) => x.wordIds.includes(id));
        if (seg) {
          const others = new Set(seg.wordIds.filter((x) => x !== id));
          words = words.map((x) => (others.has(x.id) && x.emphasized ? { ...x, emphasized: false } : x));
        }
      }
      return { words };
    }),

  setWordColor: (id, color) =>
    commit(set, get, (s) => ({
      words: s.words.map((w) =>
        w.id === id ? { ...w, colorOverride: color ?? undefined } : w,
      ),
    })),

  setWordSizeScale: (id, scale) =>
    commit(set, get, (s) => ({
      words: s.words.map((w) => (w.id === id ? { ...w, sizeScale: scale === 1 ? undefined : scale } : w)),
    })),
  setWordBold: (id, bold) =>
    commit(set, get, (s) => ({
      words: s.words.map((w) => (w.id === id ? { ...w, bold: bold || undefined } : w)),
    })),
  resetWordStyle: (id) =>
    commit(set, get, (s) => ({
      words: s.words.map((w) =>
        w.id === id ? { ...w, colorOverride: undefined, sizeScale: undefined, bold: undefined, wordStyle: undefined } : w,
      ),
    })),

  // Bulk word styling - apply/overwrite a style patch to many words at once (the
  // timeline's multi-selection). One undoable commit. Emphasis still respects
  // "one hook per caption line".
  styleWords: (ids, patch) =>
    commit(set, get, (s) => {
      if (!ids.length) return {};
      const idset = new Set(ids);
      let words = s.words.map((w) => {
        if (!idset.has(w.id)) return w;
        const nw = { ...w };
        if (patch.emphasized !== undefined) nw.emphasized = patch.emphasized || undefined;
        if (patch.bold !== undefined) nw.bold = patch.bold || undefined;
        if (patch.sizeScale !== undefined) nw.sizeScale = patch.sizeScale === 1 ? undefined : patch.sizeScale;
        if (patch.colorOverride !== undefined) nw.colorOverride = patch.colorOverride ?? undefined;
        if (patch.hidden !== undefined) nw.hidden = patch.hidden || undefined;
        if (patch.wordStyle) {
          const merged: Record<string, unknown> = { ...(w.wordStyle ?? {}) };
          for (const [k, v] of Object.entries(patch.wordStyle)) {
            if (v === null || v === undefined) delete merged[k];
            else merged[k] = v;
          }
          nw.wordStyle = Object.keys(merged).length ? (merged as WordStyle) : undefined;
        }
        return nw;
      });
      if (patch.emphasized === true) words = enforceSingleEmphasisPerSegment(words, s.segments);
      return { words };
    }),
  resetWordsStyle: (ids) =>
    commit(set, get, (s) => {
      const idset = new Set(ids);
      return {
        words: s.words.map((w) =>
          idset.has(w.id) ? { ...w, colorOverride: undefined, sizeScale: undefined, bold: undefined, wordStyle: undefined } : w,
        ),
      };
    }),

  setTemplate: (id) =>
    commit(set, get, (s) => {
      const tpl = TEMPLATES_BY_ID[id];
      if (!tpl) return {};
      return {
        templateId: id,
        segments: segmentTranscript(s.words, tpl.segmentation),
        styleOverrides: {},
        placementOverride: {},
      };
    }),

  applyEnhancement: (groups) =>
    commit(set, get, (s) => {
      if (groups.length === 0) return {};
      const byId = new Map(s.words.map((w) => [w.id, w]));
      // Overwrite emphasis: only the groups that earned a key word get one
      // (most groups have keyWordId null - emphasis is rare).
      const keyIds = new Set(groups.map((g) => g.keyWordId).filter((id): id is string => !!id));
      const words = s.words.map((w) => ({ ...w, emphasized: keyIds.has(w.id) }));

      const segments: CaptionSegment[] = groups
        .map((g) => {
          const members = g.wordIds
            .map((id) => byId.get(id))
            .filter((w): w is TranscriptWord => Boolean(w));
          if (members.length === 0) return null;
          return {
            id: localId('seg'),
            wordIds: members.map((w) => w.id),
            startMs: Math.min(...members.map((w) => w.startMs)),
            endMs: Math.max(...members.map((w) => w.endMs)),
          } satisfies CaptionSegment;
        })
        .filter((x): x is CaptionSegment => x !== null)
        .sort((a, b) => a.startMs - b.startMs);

      return { words, segments };
    }),

  updateStyle: (patch) =>
    commit(set, get, (s) => ({
      styleOverrides: deepMerge(s.styleOverrides, patch) as DeepPartial<CaptionStyle>,
    })),

  setPlacement: (patch) =>
    commit(set, get, (s) => ({
      placementOverride: { ...s.placementOverride, ...patch },
    })),

  resetStyle: () => commit(set, get, () => ({ styleOverrides: {}, placementOverride: {} })),

  // Re-group captions into fixed N-word chunks (or back to the template's
  // phrase-aware grouping when null). Emphasis/timings on the words are kept;
  // only the on-screen grouping changes.
  setWordsPerGroup: (n) =>
    commit(set, get, (s) => {
      const tpl = s.template();
      const opts =
        n == null
          ? tpl.segmentation
          : {
              maxWords: n,
              preferredWords: n,
              maxChars: 9999,
              maxLines: 4,
              pauseThresholdMs: 10_000_000,
              breakOnSentencePunctuation: false,
            };
      const segments: CaptionSegment[] = segmentTranscript(s.words, opts).map((g) => ({
        id: localId('seg'),
        wordIds: g.wordIds,
        startMs: g.startMs,
        endMs: g.endMs,
      }));
      return { wordsPerGroup: n, segments, selectedSegmentIds: [] };
    }),

  // Restore the whole project look/structure to its as-created state: default
  // template, the auto caption breaks and the rare AI emphasis, and clear every
  // customization (style/placement overrides, transitions, B-roll, hooks, audio,
  // per-word styles). Word TEXT is kept - this resets the look, not the content.
  resetToInitial: () =>
    commit(set, get, (s) => {
      const snap = s.initialSnapshot;
      const tid = snap?.templateId && TEMPLATES_BY_ID[snap.templateId] ? snap.templateId : DEFAULT_TEMPLATE_ID;
      const tpl = TEMPLATES_BY_ID[tid] ?? TEMPLATES_BY_ID[DEFAULT_TEMPLATE_ID]!;
      const emph = new Set(snap?.emphasizedWordIds ?? []);
      const words = s.words.map((w) => ({
        ...w,
        emphasized: emph.has(w.id) || undefined,
        colorOverride: undefined,
        sizeScale: undefined,
        bold: undefined,
      }));
      const clean = sanitizeWordTimings(words);
      const segments: CaptionSegment[] = snap?.segments?.length
        ? [...snap.segments]
            .sort((a, b) => a.idx - b.idx)
            .filter((seg) => seg.wordIds.length > 0)
            .map((seg) => ({ id: localId('seg'), wordIds: seg.wordIds, startMs: seg.startMs, endMs: seg.endMs }))
        : segmentTranscript(clean, tpl.segmentation);
      return {
        words: clean,
        segments,
        templateId: tid,
        styleOverrides: {},
        placementOverride: {},
        transitions: [],
        selectedTransitionId: null,
        brolls: [],
        selectedBrollId: null,
        hooks: [],
        selectedHookId: null,
        audio: { originalVolume: 1, originalMuted: false, music: null, sfx: [] },
        selectedSegmentIds: [],
        selectedWordId: null,
        selectedWordIds: [],
      };
    }),

  // Timeline transitions (undoable). Placed at a time; duration is editable.
  addTransition: (kind, startMs) =>
    commit(set, get, (s) => {
      const t: Transition = { id: localId('tr'), kind, startMs: Math.max(0, Math.round(startMs)), durationMs: 600 };
      return {
        transitions: [...s.transitions, t].sort((a, b) => a.startMs - b.startMs),
        selectedTransitionId: t.id,
      };
    }),
  updateTransition: (id, patch) =>
    commit(set, get, (s) => ({
      transitions: s.transitions
        .map((t) => (t.id === id ? { ...t, ...patch } : t))
        .sort((a, b) => a.startMs - b.startMs),
    })),
  removeTransition: (id) =>
    commit(set, get, (s) => ({
      transitions: s.transitions.filter((t) => t.id !== id),
      selectedTransitionId: s.selectedTransitionId === id ? null : s.selectedTransitionId,
    })),
  selectTransition: (id) => set({ selectedTransitionId: id }),

  // Audio. Edits mark the project dirty so the debounced autosave persists them
  // (original level/mute + external music URLs survive refresh).
  setOriginalVolume: (v) =>
    set((s) => ({ audio: { ...s.audio, originalVolume: Math.max(0, Math.min(1, v)), originalMuted: false }, saveState: 'dirty' })),
  setOriginalMuted: (m) => set((s) => ({ audio: { ...s.audio, originalMuted: m }, saveState: 'dirty' })),
  setMusic: (name, url) =>
    set((s) => {
      if (s.audio.music?.url && s.audio.music.url.startsWith('blob:')) URL.revokeObjectURL(s.audio.music.url);
      return { audio: { ...s.audio, music: { name, url, volume: 0.5, loop: true, duck: true, startMs: 0, durationMs: s.durationMs || undefined } }, saveState: 'dirty' };
    }),
  updateMusic: (patch) =>
    set((s) => (s.audio.music ? { audio: { ...s.audio, music: { ...s.audio.music, ...patch } }, saveState: 'dirty' } : {})),
  removeMusic: () =>
    set((s) => {
      if (s.audio.music?.url && s.audio.music.url.startsWith('blob:')) URL.revokeObjectURL(s.audio.music.url);
      return { audio: { ...s.audio, music: null }, saveState: 'dirty' };
    }),
  addSfx: (clip) => {
    const id = localId('sfx');
    set((s) => ({ audio: { ...s.audio, sfx: [...(s.audio.sfx ?? []), { ...clip, id }].sort((a, b) => a.startMs - b.startMs) }, saveState: 'dirty' }));
    return id;
  },
  updateSfx: (id, patch) =>
    set((s) => ({ audio: { ...s.audio, sfx: (s.audio.sfx ?? []).map((x) => (x.id === id ? { ...x, ...patch } : x)).sort((a, b) => a.startMs - b.startMs) }, saveState: 'dirty' })),
  removeSfx: (id) =>
    set((s) => ({ audio: { ...s.audio, sfx: (s.audio.sfx ?? []).filter((x) => x.id !== id) }, saveState: 'dirty' })),
  // Master output level - session only (monitoring), never persisted.
  setMasterVolume: (v) => set({ masterVolume: Math.max(0, Math.min(1, v)), masterMuted: false }),
  setMasterMuted: (m) => set({ masterMuted: m }),

  // B-roll. Edits mark the project dirty so the debounced autosave persists them
  // (only clips that finished uploading carry a storageKey and survive refresh).
  addBroll: (kind, name, url, startMs) => {
    const id = localId('br');
    set((s) => {
      const b: Broll = {
        id,
        kind,
        name,
        url,
        startMs: Math.max(0, Math.round(startMs)),
        durationMs: 3000,
        mode: 'overlay',
        x: 0.5,
        y: 0.5,
        scale: 0.5,
        fit: 'cover',
      };
      return {
        brolls: [...s.brolls, b].sort((a, c) => a.startMs - c.startMs),
        selectedBrollId: b.id,
        saveState: 'dirty',
      };
    });
    return id;
  },
  updateBroll: (id, patch) =>
    set((s) => ({
      brolls: s.brolls.map((b) => (b.id === id ? { ...b, ...patch } : b)).sort((a, c) => a.startMs - c.startMs),
      saveState: 'dirty',
    })),
  removeBroll: (id) =>
    set((s) => {
      const b = s.brolls.find((x) => x.id === id);
      if (b?.url.startsWith('blob:')) URL.revokeObjectURL(b.url);
      return {
        brolls: s.brolls.filter((x) => x.id !== id),
        selectedBrollId: s.selectedBrollId === id ? null : s.selectedBrollId,
        saveState: 'dirty',
      };
    }),
  selectBroll: (id) => set({ selectedBrollId: id }),

  // Hook moments / takeovers. No count cap (the user decides how many); only
  // one opening hook is kept (normalizeHooks demotes extras to 'power').
  addHook: (partial = {}) => {
    const id = localId('hk');
    set((s) => {
      const startMs = Math.max(0, Math.round(partial.startMs ?? 0));
      const endMs = Math.max(startMs + 400, Math.round(partial.endMs ?? startMs + 1800));
      const h: HookMoment = {
        id,
        type: partial.type ?? 'power',
        text: partial.text ?? 'HOOK',
        startMs,
        endMs,
        anchorWordId: partial.anchorWordId,
        hideCaptions: partial.hideCaptions ?? true,
        anchor: partial.anchor ?? 'center',
        scale: partial.scale ?? 1,
        color: partial.color ?? '#ffd400',
        bgColor: partial.bgColor,
        animation: partial.animation ?? 'punch',
        behind: partial.behind ?? false,
      };
      return { hooks: normalizeHooks([...s.hooks, h]), selectedHookId: id, saveState: 'dirty' };
    });
    return id;
  },
  updateHook: (id, patch) =>
    set((s) => ({
      hooks: normalizeHooks(s.hooks.map((h) => (h.id === id ? { ...h, ...patch } : h))),
      saveState: 'dirty',
    })),
  removeHook: (id) =>
    set((s) => ({
      hooks: s.hooks.filter((h) => h.id !== id),
      selectedHookId: s.selectedHookId === id ? null : s.selectedHookId,
      saveState: 'dirty',
    })),
  selectHook: (id) => set({ selectedHookId: id }),

  setLeftTab: (tab) => set({ leftTab: tab }),
  setSafeZone: (z) => set({ safeZone: z }),
  // Aspect is a project-level setting (not on the undo stack); marking dirty
  // persists it on the next save, and the export reads it live.
  setAspect: (a) => set({ aspect: a, saveState: 'dirty' }),
  setCutout: (patch) => set((s) => ({ cutout: { ...s.cutout, ...patch } })),
  setAudioEnhance: (patch) => set((s) => ({ audioEnhance: { ...s.audioEnhance, ...patch } })),
  setDepthDialogOpen: (open) => set({ depthDialogOpen: open }),
  setWatermark: (on) => set({ watermark: on }),

  // kMagic preview/apply/rollback (see the kMagic tab).
  previewMagicResult: (result) =>
    set((s) => {
      const backup = { snap: snap(s), brolls: s.brolls, audio: s.audio, hooks: s.hooks };
      const emph = new Set(result.emphasisWordIds ?? []);
      let words = s.words.map((w) =>
        emph.size ? { ...w, emphasized: emph.has(w.id) || undefined } : w,
      );
      words = enforceSingleEmphasisPerSegment(words, s.segments);
      const transitions = (result.transitions ?? []).map((t) => ({
        id: localId('tr'),
        kind: t.kind,
        startMs: Math.max(0, Math.round(t.startMs)),
        durationMs: Math.max(100, Math.round(t.durationMs)),
      }));
      const templateId =
        result.templateId && TEMPLATES_BY_ID[result.templateId] ? result.templateId : s.templateId;

      // Caption position → placementOverride (merged onto the user's current).
      const placementOverride: Partial<CaptionPlacement> = { ...s.placementOverride };
      const rp = result.placement;
      if (rp) {
        if (rp.anchor !== undefined) placementOverride.anchor = rp.anchor;
        if (rp.xNorm !== undefined) placementOverride.xNorm = rp.xNorm;
        if (rp.yNorm !== undefined) placementOverride.yNorm = rp.yNorm;
        if (rp.maxWidthNorm !== undefined) placementOverride.maxWidthNorm = rp.maxWidthNorm;
      }

      // Curated style tweaks → styleOverrides (merged onto the user's current).
      const styleOverrides: DeepPartial<CaptionStyle> = { ...s.styleOverrides };
      const so = result.styleOverrides;
      if (so) {
        if (so.color !== undefined) styleOverrides.color = so.color;
        if (so.spokenColor !== undefined) styleOverrides.spokenColor = so.spokenColor;
        if (so.textTransform !== undefined) styleOverrides.textTransform = so.textTransform;
        if (so.revealMode !== undefined) styleOverrides.revealMode = so.revealMode;
        if (so.depthLayers !== undefined) styleOverrides.depthLayers = so.depthLayers;
        if (so.palette !== undefined) styleOverrides.palette = so.palette;
        if (
          so.emphasisColor !== undefined || so.emphasisScale !== undefined ||
          so.emphasisHighlightColor !== undefined || so.emphasisBold !== undefined ||
          so.emphasisNewLine !== undefined || so.emphasisFontFamily !== undefined
        ) {
          styleOverrides.emphasis = {
            ...(styleOverrides.emphasis ?? {}),
            ...(so.emphasisColor !== undefined ? { color: so.emphasisColor } : {}),
            ...(so.emphasisScale !== undefined ? { scale: so.emphasisScale } : {}),
            ...(so.emphasisHighlightColor !== undefined ? { highlightColor: so.emphasisHighlightColor } : {}),
            ...(so.emphasisBold !== undefined ? { bold: so.emphasisBold } : {}),
            ...(so.emphasisNewLine !== undefined ? { newLine: so.emphasisNewLine } : {}),
            ...(so.emphasisFontFamily !== undefined ? { fontFamily: so.emphasisFontFamily } : {}),
          };
        }
        if (so.background) {
          styleOverrides.background = {
            ...(styleOverrides.background ?? {}),
            ...(so.background.mode !== undefined ? { mode: so.background.mode } : {}),
            ...(so.background.color !== undefined ? { color: so.background.color } : {}),
          };
        }
        // Active (spoken) word color, with a contrast guard: when the AI recolors
        // an activeWord/perWord BOX, the template's active-word text color (tuned
        // for the old box) can end up unreadable (e.g. dark text on a new dark
        // box). If the AI didn't set an explicit active color, force a readable
        // one so the spoken word never disappears.
        {
          const tpl = TEMPLATES_BY_ID[templateId];
          let activeColor = so.activeWordColor;
          const boxColor = so.background?.color;
          const effMode = so.background?.mode ?? tpl?.style.background.mode;
          if (!activeColor && boxColor && (effMode === 'activeWord' || effMode === 'perWord')) {
            const tplActive = tpl?.style.activeWord.color;
            if (!tplActive || Math.abs(colorLuminance(tplActive) - colorLuminance(boxColor)) < 0.3) {
              activeColor = readableTextColor(boxColor);
            }
          }
          if (activeColor) {
            styleOverrides.activeWord = { ...(styleOverrides.activeWord ?? {}), color: activeColor };
          }
        }
        if (so.stroke) {
          // stroke is a full StrokeSpec (color required). enabled:false → keep
          // color but zero width (disables the outline); otherwise give a
          // visible width and the chosen/current color.
          const curColor = s.styleOverrides.stroke?.color ?? '#000000';
          const curWidth = s.styleOverrides.stroke?.widthPx || 10;
          if (so.stroke.enabled === false) {
            styleOverrides.stroke = { color: so.stroke.color ?? curColor, widthPx: 0 };
          } else if (so.stroke.color !== undefined || so.stroke.enabled === true) {
            styleOverrides.stroke = { color: so.stroke.color ?? curColor, widthPx: curWidth };
          }
        }
      }

      // Real stock B-roll the worker fetched → place it on the timeline.
      const brolls: Broll[] = (result.brolls ?? []).map((b) => ({
        id: localId('br'),
        kind: b.kind,
        name: b.name,
        url: b.url ?? '',
        storageKey: b.storageKey,
        startMs: b.startMs,
        durationMs: b.durationMs,
        mode: b.mode,
        x: 0.5,
        y: 0.5,
        scale: b.mode === 'overlay' ? 0.6 : 1,
        fit: 'cover',
      }));

      // Audio: apply the AI's level choices; attach a fetched music track if any.
      const ra = result.audio;
      let audio: AudioSettings = {
        ...s.audio,
        ...(ra?.originalVolume !== undefined ? { originalVolume: ra.originalVolume } : {}),
        ...(ra?.originalMuted !== undefined ? { originalMuted: ra.originalMuted } : {}),
        // Timed sound effects the worker fetched → placed on the timeline.
        sfx: (result.sfx ?? []).map((x) => ({
          id: localId('sfx'),
          name: x.name,
          url: x.url ?? '',
          storageKey: x.storageKey,
          startMs: Math.max(0, Math.round(x.startMs)),
          durationMs: Math.max(200, Math.round(x.durationMs)),
          volume: x.volume ?? 0.9,
        })),
      };
      if (result.music) {
        audio = {
          ...audio,
          music: {
            name: result.music.name,
            url: result.music.url,
            volume: ra?.musicVolume ?? 0.45,
            loop: ra?.musicLoop ?? true,
            duck: ra?.musicDuck ?? true,
            startMs: 0,
            durationMs: s.durationMs || undefined,
          },
        };
      } else if (audio.music && ra) {
        // No new track, but the AI adjusted levels on the existing one.
        audio = {
          ...audio,
          music: {
            ...audio.music,
            ...(ra.musicVolume !== undefined ? { volume: ra.musicVolume } : {}),
            ...(ra.musicLoop !== undefined ? { loop: ra.musicLoop } : {}),
            ...(ra.musicDuck !== undefined ? { duck: ra.musicDuck } : {}),
          },
        };
      }

      // Big power-word takeovers the AI proposed → replace the hook list.
      const hooks: HookMoment[] = normalizeHooks(
        (result.hooks ?? []).map((h) => ({
          id: localId('hk'),
          type: h.type,
          text: h.text,
          startMs: Math.max(0, Math.round(h.startMs)),
          endMs: Math.max(Math.round(h.startMs) + 400, Math.round(h.endMs)),
          anchorWordId: h.anchorWordId,
          hideCaptions: h.hideCaptions ?? true,
          anchor: h.anchor ?? 'center',
          scale: h.scale ?? 1,
          color: h.color ?? '#ffffff',
          bgColor: h.bgColor,
          animation: h.animation ?? 'punch',
          behind: h.behind ?? false,
          offsetYNorm: h.offsetYNorm,
          // Hook STYLE the AI chose (resolved fields).
          fontFamily: h.fontFamily,
          fontWeight: h.fontWeight,
          stroke: h.stroke,
          gradient: h.gradient,
          glow: h.glow,
          fill: h.fill,
        })),
      );
      return {
        magicPreview: { backup },
        words,
        templateId,
        placementOverride,
        styleOverrides,
        transitions,
        brolls,
        audio,
        hooks,
        selectedHookId: null,
        saveState: 'dirty',
      };
    }),
  commitMagicPreview: () => set({ magicPreview: null, saveState: 'dirty' }),
  rollbackMagicPreview: () =>
    set((s) => {
      if (!s.magicPreview) return {};
      const { snap: b, brolls, audio, hooks } = s.magicPreview.backup;
      return {
        magicPreview: null,
        words: b.words,
        segments: b.segments,
        templateId: b.templateId,
        styleOverrides: b.styleOverrides,
        placementOverride: b.placementOverride,
        transitions: b.transitions,
        brolls,
        audio,
        hooks,
        selectedBrollId: null,
        selectedHookId: null,
        saveState: 'dirty',
      };
    }),

  splitSegmentAt: (segmentId, afterWordIndex) =>
    commit(set, get, (s) => {
      const idx = s.segments.findIndex((seg) => seg.id === segmentId);
      if (idx === -1) return {};
      const res = splitSeg(s.segments[idx]!, afterWordIndex, s.words);
      const segments = [...s.segments.slice(0, idx), ...res, ...s.segments.slice(idx + 1)];
      return { segments };
    }),

  mergeSegmentWithNext: (segmentId) =>
    commit(set, get, (s) => {
      const idx = s.segments.findIndex((seg) => seg.id === segmentId);
      if (idx === -1 || idx === s.segments.length - 1) return {};
      const merged = mergeSeg(s.segments[idx]!, s.segments[idx + 1]!, s.words);
      const segments = [...s.segments.slice(0, idx), merged, ...s.segments.slice(idx + 2)];
      return { segments };
    }),

  editSegmentText: (segmentId, newText) =>
    commit(set, get, (s) => {
      const seg = s.segments.find((x) => x.id === segmentId);
      if (!seg) return {};
      const byId = new Map(s.words.map((w) => [w.id, w]));
      const memberIds = new Set(seg.wordIds);
      const memberWords = seg.wordIds
        .map((id) => byId.get(id))
        .filter((w): w is TranscriptWord => Boolean(w));
      if (memberWords.length === 0) return {};

      const newWords = resplitSegment(memberWords, newText);

      // Splice the new words in where the old member words were, preserving any
      // interleaved words (e.g. hidden fillers stay put).
      const firstIdx = s.words.findIndex((w) => w.id === memberWords[0]!.id);
      const insertAt = s.words.slice(0, firstIdx).filter((w) => !memberIds.has(w.id)).length;
      const withoutMembers = s.words.filter((w) => !memberIds.has(w.id));
      const words = [
        ...withoutMembers.slice(0, insertAt),
        ...newWords,
        ...withoutMembers.slice(insertAt),
      ];

      let segments: CaptionSegment[];
      if (newWords.length === 0) {
        // Emptied the line → drop the segment.
        segments = s.segments.filter((x) => x.id !== segmentId);
      } else {
        const updated: CaptionSegment = {
          ...seg,
          wordIds: newWords.map((w) => w.id),
          startMs: newWords[0]!.startMs,
          endMs: newWords[newWords.length - 1]!.endMs,
        };
        segments = s.segments.map((x) => (x.id === segmentId ? updated : x));
      }
      return { words, segments };
    }),

  insertSegmentAfter: (segmentId) => {
    const s = get();
    const idx = s.segments.findIndex((x) => x.id === segmentId);
    if (idx === -1) return null;
    const cur = s.segments[idx]!;
    const next = s.segments[idx + 1];
    const start = cur.endMs;
    let end = next ? next.startMs : s.durationMs || cur.endMs + 2000;
    if (end - start < 400) end = start + 400; // ensure a usable window

    const wid = localId('w');
    const newWord: TranscriptWord = { id: wid, text: 'New caption', startMs: start, endMs: end };
    // Place the word right after the current segment's last word.
    const lastWordId = cur.wordIds[cur.wordIds.length - 1];
    const lastIdx = lastWordId ? s.words.findIndex((w) => w.id === lastWordId) : -1;
    const at = lastIdx === -1 ? s.words.length : lastIdx + 1;
    const words = [...s.words.slice(0, at), newWord, ...s.words.slice(at)];

    const newSeg: CaptionSegment = {
      id: localId('seg'),
      wordIds: [wid],
      startMs: start,
      endMs: end,
    };
    const segments = [...s.segments.slice(0, idx + 1), newSeg, ...s.segments.slice(idx + 1)];

    set({
      words,
      segments,
      past: [...s.past, snap(s)].slice(-HISTORY_LIMIT),
      future: [],
      saveState: 'dirty',
    });
    return newSeg.id;
  },

  setTimingMode: (mode) => set({ timingMode: mode }),

  setSegmentTiming: (segmentId, patch) =>
    commit(set, get, (s) => computeSegmentTiming(s, segmentId, patch)),

  // Timeline drag: snapshot once at drag start, then update live (no per-move
  // history entry), so the whole drag is a single undo step.
  beginTimingDrag: () =>
    set((s) => ({
      past: [...s.past, snap(s)].slice(-HISTORY_LIMIT),
      future: [],
      saveState: 'dirty',
    })),
  dragSegmentTiming: (segmentId, patch) =>
    set((s) => computeSegmentTiming(s, segmentId, patch) as Partial<EditorState>),

  deleteSegment: (segmentId) =>
    commit(set, get, (s) => {
      const seg = s.segments.find((x) => x.id === segmentId);
      if (!seg) return {};
      const memberIds = new Set(seg.wordIds);
      return {
        words: s.words.filter((w) => !memberIds.has(w.id)),
        segments: s.segments.filter((x) => x.id !== segmentId),
        selectedWordId: null,
      };
    }),

  undo: () =>
    set((s) => {
      const prev = s.past[s.past.length - 1];
      if (!prev) return s;
      return {
        ...s,
        ...prev,
        past: s.past.slice(0, -1),
        future: [snap(s), ...s.future].slice(0, HISTORY_LIMIT),
        saveState: 'dirty',
      };
    }),

  redo: () =>
    set((s) => {
      const next = s.future[0];
      if (!next) return s;
      return {
        ...s,
        ...next,
        past: [...s.past, snap(s)].slice(-HISTORY_LIMIT),
        future: s.future.slice(1),
        saveState: 'dirty',
      };
    }),

  markSaved: () => set({ saveState: 'saved' }),
}));

/** Apply a mutation, pushing the prior snapshot onto the undo stack. */
function commit(
  set: (partial: Partial<EditorState> | ((s: EditorState) => Partial<EditorState>)) => void,
  get: () => EditorState,
  fn: (s: EditorState) => Partial<EditorState>,
): void {
  const s = get();
  const before = snap(s);
  const patch = fn(s);
  if (Object.keys(patch).length === 0) return;
  // Invariant: at most one emphasized word per caption window. Re-grouping (e.g.
  // switching template) can put two key words in one segment; normalize here so
  // every mutation upholds it in one place (render + sidebar stay consistent).
  const nextWords = patch.words ?? s.words;
  const nextSegments = patch.segments ?? s.segments;
  const normalizedWords = enforceSingleEmphasisPerSegment(nextWords, nextSegments);
  set({
    ...patch,
    ...(normalizedWords !== nextWords ? { words: normalizedWords } : {}),
    past: [...s.past, before].slice(-HISTORY_LIMIT),
    future: [],
    saveState: 'dirty',
  });
}
